import { forwardRef, type Ref, type TextareaHTMLAttributes } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import type { TextAreaState } from './types';

export interface TextAreaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  state?: keyof typeof TextAreaState;
}

const textarea = variants({
  base: [
    'outline',
    'outline-1',
    'outline-offset-[-1px]',
    'outline-emphasis-subtle',
    'focus:ring-none',
    'text-body-sm',
    'rounded-xs',
    'p-md',
    'bg-emphasis-default',
    'text-strong',
    'placeholder:text-subtle',
    'resize-none',
    'disabled:cursor-not-allowed',
    'disabled:pointer-events-none',
    'disabled:outline-disabled',
    'disabled:outline-opacity-subtle',
    'disabled:outline-px',
    'disabled:text-disabled',
    'read-only:cursor-not-allowed',
    'read-only:pointer-events-none',
    'read-only:outline-disabled',
    'read-only:outline-opacity-subtle',
    'read-only:outline-px',
    'read-only:text-disabled',
    'hover:outline-opacity-stronger',
    'leading-5',
  ],
  variants: {
    state: {
      default: [
        'focus:outline-secondary-default',
        'hover:outline-disabled',
        'focus:outline-2',
        'hover:outline-2',
        'hover:outline-offset-[-2px]',
        'focus:outline-offset-[-2px]',
      ],
      error: [
        'outline',
        'outline-error-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
      warning: [
        'outline',
        'outline-warning-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
      success: [
        'outline',
        'outline-success-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
    },
  },
});

const TextArea = forwardRef(
  (
    { state = 'default', className, ...props }: TextAreaProps,
    ref: Ref<HTMLTextAreaElement>,
  ) => {
    const stateClasses = textarea({ state });

    return (
      <textarea
        ref={ref}
        rows={5}
        className={cn(stateClasses, className)}
        aria-invalid={state === 'error'}
        {...props}
      />
    );
  },
);

TextArea.displayName = 'TextArea';

export { TextArea };
